import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Loader2, Plus, Phone, Mail, MapPin, Building2, Star, Globe, Award, Tag, Filter, X, SlidersHorizontal, Sparkles } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { ServiceProvider } from './types';
import { SUPABASE_URL } from '../../lib/constants';

const ServiceProvidersTab = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  
  // Filter states
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterRating, setFilterRating] = useState<string>('all');
  const [filterPreferred, setFilterPreferred] = useState<boolean | null>(null);
  const [filterDataQuality, setFilterDataQuality] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [enrichingProviderId, setEnrichingProviderId] = useState<string | null>(null);
  
  const [createFormData, setCreateFormData] = useState({
    company_name: '',
    email: '',
    phone: '',
    address: '',
    notes: '',
    website: '',
    primary_category: ''
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_providers')
        .select('*')
        .order('company_name', { ascending: true });

      if (error) throw error;
      setProviders(data || []);
    } catch (error: any) {
      console.error('Error fetching service providers:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEnrichProvider = async (e: React.MouseEvent, providerId: string) => {
    e.stopPropagation(); // Prevent card click navigation
    
    try {
      setEnrichingProviderId(providerId);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${SUPABASE_URL}/functions/v1/enrich-service-provider`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ service_provider_id: providerId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start enrichment');
      }

      // Navigate to detail page to see progress
      navigate(`/admin/service-provider/${providerId}`);
    } catch (error: any) {
      console.error('Error starting enrichment:', error);
      alert(`Failed to start enrichment: ${error.message}`);
    } finally {
      setEnrichingProviderId(null);
    }
  };

  const handleCreateProvider = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);

    try {
      const { data, error } = await supabase
        .from('service_providers')
        .insert([{
          company_name: createFormData.company_name,
          email: createFormData.email || null,
          phone: createFormData.phone || null,
          address: createFormData.address || null,
          notes: createFormData.notes || null,
          website: createFormData.website || null,
          primary_category: createFormData.primary_category || null
        }])
        .select()
        .single();

      if (error) throw error;

      // Reset form and close modal
      setCreateFormData({
        company_name: '',
        email: '',
        phone: '',
        address: '',
        notes: '',
        website: '',
        primary_category: ''
      });
      setShowCreateModal(false);

      // Refresh the providers list
      fetchProviders();
    } catch (error: any) {
      console.error('Error creating service provider:', error);
      setError(error.message);
    } finally {
      setCreating(false);
    }
  };

  // Get unique categories for filter dropdown
  const uniqueCategories = useMemo(() => {
    const categories = new Set<string>();
    providers.forEach(provider => {
      if (provider.primary_category) {
        categories.add(provider.primary_category);
      }
    });
    return Array.from(categories).sort();
  }, [providers]);

  // Apply all filters and sorting
  const filteredProviders = useMemo(() => {
    let filtered = providers.filter(provider => {
      // Text search
      const search = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || (
        provider.company_name.toLowerCase().includes(search) ||
        provider.email?.toLowerCase().includes(search) ||
        provider.phone?.toLowerCase().includes(search) ||
        provider.primary_category?.toLowerCase().includes(search) ||
        provider.city?.toLowerCase().includes(search) ||
        provider.state?.toLowerCase().includes(search)
      );
      
      // Status filter
      const matchesStatus = filterStatus === 'all' || provider.status === filterStatus;
      
      // Category filter
      const matchesCategory = filterCategory === 'all' || provider.primary_category === filterCategory;
      
      // Rating filter
      let matchesRating = true;
      if (filterRating !== 'all') {
        const minRating = parseFloat(filterRating);
        matchesRating = provider.rating !== null && provider.rating >= minRating;
      }
      
      // Preferred filter
      const matchesPreferred = filterPreferred === null || provider.preferred_provider === filterPreferred;
      
      // Data quality filter
      let matchesDataQuality = true;
      if (filterDataQuality !== 'all') {
        if (provider.data_quality_score !== null && provider.data_quality_score !== undefined) {
          switch (filterDataQuality) {
            case 'high':
              matchesDataQuality = provider.data_quality_score >= 0.8;
              break;
            case 'medium':
              matchesDataQuality = provider.data_quality_score >= 0.5 && provider.data_quality_score < 0.8;
              break;
            case 'low':
              matchesDataQuality = provider.data_quality_score < 0.5;
              break;
          }
        } else {
          matchesDataQuality = false;
        }
      }
      
      return matchesSearch && matchesStatus && matchesCategory && matchesRating && matchesPreferred && matchesDataQuality;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.company_name.localeCompare(b.company_name);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'reviews':
          return (b.reviews_count || 0) - (a.reviews_count || 0);
        case 'quality':
          return (b.data_quality_score || 0) - (a.data_quality_score || 0);
        case 'recent':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [providers, searchTerm, filterStatus, filterCategory, filterRating, filterPreferred, filterDataQuality, sortBy]);

  // Check if any filters are active
  const hasActiveFilters = filterStatus !== 'all' || 
                          filterCategory !== 'all' || 
                          filterRating !== 'all' || 
                          filterPreferred !== null || 
                          filterDataQuality !== 'all';

  const clearAllFilters = () => {
    setFilterStatus('all');
    setFilterCategory('all');
    setFilterRating('all');
    setFilterPreferred(null);
    setFilterDataQuality('all');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6 flex flex-col flex-1 min-h-0 overflow-y-auto">
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Wrench className="w-6 h-6 text-gray-400" />
              <h2 className="text-xl font-semibold">Service Providers</h2>
              <span className="text-sm text-gray-500">({filteredProviders.length} of {providers.length})</span>
            </div>
          </div>
          
          {/* Search and Filter Bar */}
          <div className="space-y-3 mb-3">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                placeholder="Search providers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black text-sm"
              />
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center space-x-2 px-3 py-2 border rounded-md transition-colors ${
                  showFilters || hasActiveFilters 
                    ? 'bg-black text-white border-black' 
                    : 'text-gray-600 hover:text-gray-800 border-gray-300'
                }`}
                title="Filters"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="text-sm">Filters</span>
                {hasActiveFilters && !showFilters && (
                  <span className="bg-white text-black rounded-full w-5 h-5 flex items-center justify-center text-xs font-medium">
                    {[filterStatus !== 'all', filterCategory !== 'all', filterRating !== 'all', filterPreferred !== null, filterDataQuality !== 'all'].filter(Boolean).length}
                  </span>
                )}
              </button>
              <button
                onClick={fetchProviders}
                disabled={loading}
                className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 border border-gray-300 rounded-md"
                title="Refresh"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Loader2 className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm flex items-center space-x-2">
                    <Filter className="w-4 h-4" />
                    <span>Filter Options</span>
                  </h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="text-xs text-red-600 hover:text-red-700 flex items-center space-x-1"
                    >
                      <X className="w-3 h-3" />
                      <span>Clear All</span>
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending_verification">Pending Verification</option>
                      <option value="blacklisted">Blacklisted</option>
                    </select>
                  </div>

                  {/* Category Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="all">All Categories</option>
                      {uniqueCategories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  {/* Rating Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Min. Rating
                    </label>
                    <select
                      value={filterRating}
                      onChange={(e) => setFilterRating(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="all">Any Rating</option>
                      <option value="4.5">4.5+ Stars</option>
                      <option value="4.0">4.0+ Stars</option>
                      <option value="3.5">3.5+ Stars</option>
                      <option value="3.0">3.0+ Stars</option>
                    </select>
                  </div>

                  {/* Data Quality Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Data Quality
                    </label>
                    <select
                      value={filterDataQuality}
                      onChange={(e) => setFilterDataQuality(e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="all">Any Quality</option>
                      <option value="high">High (80%+)</option>
                      <option value="medium">Medium (50-79%)</option>
                      <option value="low">Low (&lt;50%)</option>
                    </select>
                  </div>

                  {/* Preferred Filter */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Preferred
                    </label>
                    <select
                      value={filterPreferred === null ? 'all' : filterPreferred.toString()}
                      onChange={(e) => setFilterPreferred(e.target.value === 'all' ? null : e.target.value === 'true')}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                    >
                      <option value="all">All Providers</option>
                      <option value="true">Preferred Only</option>
                      <option value="false">Non-Preferred</option>
                    </select>
                  </div>
                </div>

                {/* Sort Options */}
                <div className="pt-3 border-t border-gray-200">
                  <label className="block text-xs font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'name', label: 'Name (A-Z)' },
                      { value: 'rating', label: 'Highest Rating' },
                      { value: 'reviews', label: 'Most Reviews' },
                      { value: 'quality', label: 'Data Quality' },
                      { value: 'recent', label: 'Recently Added' }
                    ].map(option => (
                      <button
                        key={option.value}
                        onClick={() => setSortBy(option.value)}
                        className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                          sortBy === option.value
                            ? 'bg-black text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-100'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Active Filter Badges */}
            {hasActiveFilters && !showFilters && (
              <div className="flex flex-wrap gap-2">
                {filterStatus !== 'all' && (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
                    <span>Status: {filterStatus.replace('_', ' ')}</span>
                    <button onClick={() => setFilterStatus('all')} className="hover:text-blue-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filterCategory !== 'all' && (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-md text-xs">
                    <span>Category: {filterCategory}</span>
                    <button onClick={() => setFilterCategory('all')} className="hover:text-purple-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filterRating !== 'all' && (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded-md text-xs">
                    <span>Rating: {filterRating}+</span>
                    <button onClick={() => setFilterRating('all')} className="hover:text-yellow-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filterPreferred !== null && (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-800 rounded-md text-xs">
                    <span>{filterPreferred ? 'Preferred Only' : 'Non-Preferred'}</span>
                    <button onClick={() => setFilterPreferred(null)} className="hover:text-green-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {filterDataQuality !== 'all' && (
                  <span className="inline-flex items-center space-x-1 px-2 py-1 bg-orange-100 text-orange-800 rounded-md text-xs">
                    <span>Quality: {filterDataQuality}</span>
                    <button onClick={() => setFilterDataQuality('all')} className="hover:text-orange-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Provider</span>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        {filteredProviders.length === 0 ? (
          <div className="text-center py-8">
            <Wrench className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Service Providers Found</h3>
            <p className="text-gray-600 mb-4">
              {searchTerm || hasActiveFilters
                ? 'No service providers match your search or filters.'
                : 'Add service providers to track contractors and service companies.'}
            </p>
            {(searchTerm || hasActiveFilters) && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  clearAllFilters();
                }}
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Clear search and filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProviders.map((provider) => (
              <div
                key={provider.id}
                onClick={() => navigate(`/admin/service-provider/${provider.id}`)}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-gray-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2 flex-1 min-w-0">
                    <Building2 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <h3 className="font-medium text-lg truncate">{provider.company_name}</h3>
                  </div>
                  {provider.preferred_provider && (
                    <Award className="w-5 h-5 text-yellow-500 flex-shrink-0" title="Preferred Provider" />
                  )}
                </div>

                {/* Status and Category Badges */}
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  {provider.primary_category && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      <Tag className="w-3 h-3 mr-1" />
                      {provider.primary_category}
                    </span>
                  )}
                  {provider.status && provider.status !== 'active' && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      provider.status === 'pending_verification' ? 'bg-yellow-100 text-yellow-800' :
                      provider.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {provider.status.replace('_', ' ')}
                    </span>
                  )}
                </div>

                {/* Rating */}
                {provider.rating && (
                  <div className="flex items-center space-x-1 mb-3">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    <span className="text-sm font-medium">{provider.rating.toFixed(1)}</span>
                    {provider.reviews_count && provider.reviews_count > 0 && (
                      <span className="text-xs text-gray-500">({provider.reviews_count} reviews)</span>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  {provider.phone && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span>{provider.phone}</span>
                    </div>
                  )}
                  {provider.email && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{provider.email}</span>
                    </div>
                  )}
                  {provider.website && (
                    <>
                      <div className="flex items-center space-x-2 text-sm text-blue-600 min-w-0">
                        <Globe className="w-4 h-4 flex-shrink-0" />
                        <span className="truncate">{provider.website}</span>
                      </div>
                      <button
                        onClick={(e) => handleEnrichProvider(e, provider.id)}
                        disabled={enrichingProviderId === provider.id}
                        className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-50 text-blue-600 hover:bg-blue-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-fit"
                        title="Enrich provider data from website"
                      >
                        {enrichingProviderId === provider.id ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            <span>Enriching...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3 h-3" />
                            <span>Enrich</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
                  {(provider.city || provider.state) && (
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span className="truncate">
                        {[provider.city, provider.state].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {!provider.phone && !provider.email && !provider.website && !provider.city && !provider.state && (
                    <div className="text-sm text-gray-400 italic">
                      No contact information
                    </div>
                  )}
                </div>

                {/* Data Quality Score */}
                {provider.data_quality_score !== null && provider.data_quality_score !== undefined && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Data Quality</span>
                      <span className={`font-medium ${
                        provider.data_quality_score >= 0.8 ? 'text-green-600' :
                        provider.data_quality_score >= 0.5 ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {Math.round(provider.data_quality_score * 100)}%
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      </div>

      {/* Create Provider Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold">Add Service Provider</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <span className="text-2xl">&times;</span>
              </button>
            </div>

            <form onSubmit={handleCreateProvider} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={createFormData.company_name}
                  onChange={(e) => setCreateFormData({ ...createFormData, company_name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="ABC Plumbing Services"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Category
                </label>
                <input
                  type="text"
                  value={createFormData.primary_category}
                  onChange={(e) => setCreateFormData({ ...createFormData, primary_category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="e.g., Plumbing, HVAC, Electrical"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={createFormData.email}
                  onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="contact@abcplumbing.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={createFormData.phone}
                  onChange={(e) => setCreateFormData({ ...createFormData, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={createFormData.website}
                  onChange={(e) => setCreateFormData({ ...createFormData, website: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  value={createFormData.address}
                  onChange={(e) => setCreateFormData({ ...createFormData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="123 Main St, City, State 12345"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={createFormData.notes}
                  onChange={(e) => setCreateFormData({ ...createFormData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
                  placeholder="Additional information about this provider..."
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm">{error}</p>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="flex items-center space-x-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                >
                  {creating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Creating...</span>
                    </>
                  ) : (
                    <span>Add Provider</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default ServiceProvidersTab;

